// ESEMÉNYNAPLÓ — entitás-típus → IKON. Egy fogalom = egy jel, az EGÉSZ felületen.
//
// A SZABÁLY (user-döntés): a napló entitás-jelvénye ugyanazt az ikont viseli, amit ugyanaz a fogalom a
// MENÜBEN vagy az adott lap FÜLÉN visel. Amihez nincs menü-/fül-megfelelő (komment-reakció, tudásbázis,
// platform-entitások), ahhoz új, ELTÉRŐ jelet választunk.
//
// A LELET, ami miatt ez a fájl külön él (user jelentette): a korábbi, CSALÁD-SZINTŰ regex-tábla hét jelre
// húzta össze a ~60 típust, és a jelek jó része MÁST jelentett máshol a felületen:
//   • a Fejlesztés `TagIcon`-t kapott (a menüben hangosbemondó),
//   • a jegy-hozzászólás és a komment-reakció UGYANAZT a `LifebuoyIcon`-t — ami a menüben az
//     ÜGYFÉLSZOLGÁLATI CSAPATOK jele,
//   • a `CubeIcon` egyszerre volt szolgáltatás, bankkártya, support-csapat és widget — miközben a menüben
//     az ESZKÖZÖK-é,
//   • az Ügyfél és a Fiók egy jelet vitt, a Számla pedig `DocumentTextIcon`-t (a menüben `ReceiptPercent`).
// Mérve: ~22 fogalom-csoportból 3 egyezett a menüvel/füllel.
//
// MIÉRT TÉTELES TÁBLA (és nem regex-családok): a család-minta pont azt a hibát termeli újra, ami miatt a
// tábla készült — egy új típus csendben beleesik valamelyik mintába, és rossz jelet kap. A tételes tábla
// mellett a hiány LÁTHATÓ, és a fal (`entity-icons.wall.spec.ts`) meg is követeli: minden `activityLog.entity.*`
// címkéhez tartozik ikon.
import {
  AcademicCapIcon,
  ArrowTrendingDownIcon,
  BellIcon,
  BoltIcon,
  BookOpenIcon,
  BriefcaseIcon,
  BuildingLibraryIcon,
  BuildingOffice2Icon,
  CalendarIcon,
  ChartBarIcon,
  ChatBubbleBottomCenterTextIcon,
  ChatBubbleLeftRightIcon,
  CircleStackIcon,
  ClockIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  CubeIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  FaceSmileIcon,
  FlagIcon,
  FolderOpenIcon,
  IdentificationIcon,
  LifebuoyIcon,
  MegaphoneIcon,
  PaperClipIcon,
  PencilSquareIcon,
  PuzzlePieceIcon,
  QuestionMarkCircleIcon,
  ReceiptPercentIcon,
  RectangleGroupIcon,
  Square3Stack3DIcon,
  SquaresPlusIcon,
  TagIcon,
  TicketIcon,
  UserCircleIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';

export type EntityIcon = ComponentType<SVGProps<SVGSVGElement>>;

/**
 * Az ISMERETLEN típus jele. SZÁNDÉKOSAN semleges („valamilyen rekord"), és SZÁNDÉKOSAN nem `TagIcon`: az
 * utóbbi a tudásbázis KATEGÓRIÁJÁÉ — egy fallback, ami mást jelent, pont a régi hibát hozná vissza.
 * A gyakorlatban nem látszik: a jelvény csak lokalizált címke mellett renderel, és a fal minden címkéhez
 * ikont követel.
 */
export const FALLBACK_ENTITY_ICON: EntityIcon = CircleStackIcon;

/**
 * Típus → ikon. A megjegyzés azt mondja meg, HONNAN jön a jel (melyik menüpont vagy fül viseli ugyanezt),
 * illetve hogy miért ÚJ a választás.
 */
export const ENTITY_ICONS: Record<string, EntityIcon> = {
  // ── Ügyfélkapcsolat ───────────────────────────────────────────────────────────────────────────────
  client: BriefcaseIcon, // menü: Ügyfelek
  clients: BriefcaseIcon,
  account: BuildingOffice2Icon, // menü: Fiókok
  accounts: BuildingOffice2Icon,
  'account-domains': BuildingOffice2Icon,
  'account-affiliations': BuildingOffice2Icon,
  contact: UserCircleIcon, // menü: Kapcsolattartók
  contacts: UserCircleIcon,
  'payment-cards': CreditCardIcon, // menü: Bankkártyák
  'billing-profiles': IdentificationIcon, // menü: Számlázási profilok

  // ── Szolgáltatások ────────────────────────────────────────────────────────────────────────────────
  services: Square3Stack3DIcon, // menü + fül: Szolgáltatások / Hozzárendelések
  'service-plans': Square3Stack3DIcon,
  'service-plan-prices': Square3Stack3DIcon,
  'service-plan-assignments': Square3Stack3DIcon,
  'service-plan-assignment-prices': Square3Stack3DIcon,
  'service-plan-assignment-users': Square3Stack3DIcon,
  'assignment-accounts': Square3Stack3DIcon,
  'service-usage': ChartBarIcon, // menü: Használati adatok
  widgets: SquaresPlusIcon, // ÚJ: nincs menü-megfelelője (beágyazható elem)

  // ── Számlázás / pénzügy ───────────────────────────────────────────────────────────────────────────
  invoice: ReceiptPercentIcon, // menü + fül: Számlák
  invoices: ReceiptPercentIcon,
  'invoice-items': ReceiptPercentIcon,
  'invoice-accounts': ReceiptPercentIcon,
  'invoice-generation-skips': ExclamationTriangleIcon, // a Számlák lap „Kihagyások" füle
  issuers: BuildingLibraryIcon, // ÚJ: a kibocsátó cég (számlatömb)
  expense: ArrowTrendingDownIcon, // a Pénzügy lap „Kiadások" füle
  expenses: ArrowTrendingDownIcon,
  'expense-category': FolderOpenIcon, // a kiadás-kategória kezelő ikonja
  'expense-categories': FolderOpenIcon,
  campaigns: FlagIcon, // menü: Kampány-előrejelzés

  // ── Ügyfélszolgálat ───────────────────────────────────────────────────────────────────────────────
  ticket: TicketIcon, // menü: Jegyek
  tickets: TicketIcon,
  'ticket-contact-access': TicketIcon,
  'ticket-attachments': PaperClipIcon, // csatolmány-család (lásd lent)
  'ticket-comments': ChatBubbleBottomCenterTextIcon, // ÚJ: hozzászólás — NEM a levelező buborék-párja
  'ticket-activity': BoltIcon, // a jegy utolsó mozgása (platform-bélyeg)
  'ticket-reads': EyeIcon, // ki mikor nyitotta meg a jegyet
  comments: ChatBubbleBottomCenterTextIcon,
  'ticket-comment-reactions': FaceSmileIcon, // ÚJ: reakció
  'ticket-comment-templates': DocumentDuplicateIcon, // ÚJ: kész válasz-sablon
  'support-teams': LifebuoyIcon, // menü: Ügyfélszolgálati csapatok
  'support-team-members': LifebuoyIcon,

  // ── Kommunikáció ──────────────────────────────────────────────────────────────────────────────────
  message: ChatBubbleLeftRightIcon, // menü: Üzenetek
  messages: ChatBubbleLeftRightIcon,
  'message-reads': ChatBubbleLeftRightIcon,
  'message-revisions': ChatBubbleLeftRightIcon,
  'message-recipient-contacts': ChatBubbleLeftRightIcon,
  'message-recipient-accounts': ChatBubbleLeftRightIcon,
  'message-recipient-employees': ChatBubbleLeftRightIcon,
  'message-recipient-departments': ChatBubbleLeftRightIcon,
  'message-recipient-teams': ChatBubbleLeftRightIcon,
  'message-recipient-services': ChatBubbleLeftRightIcon,
  'message-attachments': PaperClipIcon,
  meeting: CalendarIcon, // menü: Megbeszélések
  meetings: CalendarIcon,
  'meeting-participants': CalendarIcon,
  'meeting-series': CalendarIcon,
  notification: BellIcon, // az app értesítés-harangja
  notifications: BellIcon,
  'product-updates': MegaphoneIcon, // menü: Fejlesztések
  'product-update-attachments': PaperClipIcon,

  // ── Munkatársak / szervezet ───────────────────────────────────────────────────────────────────────
  employee: UsersIcon, // menü: Munkatársak
  employees: UsersIcon,
  'employee-details': UsersIcon,
  department: RectangleGroupIcon, // a Szervezet lap „Részlegek" füle
  departments: RectangleGroupIcon,
  team: UserGroupIcon, // a Szervezet lap „Csapatok" füle
  teams: UserGroupIcon,
  // KIVÉTEL, indoklással: a „Beosztások" fül ikonja `BriefcaseIcon`, azt viszont a MENÜBEN az ÜGYFELEK
  // viselik — egy táblán belül a kettő pont az a jel-ütközés lenne, ami miatt ez a tábla készült. A
  // beosztás ezért kap saját, szabad jelet.
  positions: AcademicCapIcon,
  'identity-profiles': Cog6ToothIcon, // ÚJ: a saját fiók beállításai

  // ── Eszközök ──────────────────────────────────────────────────────────────────────────────────────
  asset: CubeIcon, // menü + fül: Eszközök / Leltár
  assets: CubeIcon,
  'asset-events': CubeIcon,
  'asset-categories': CubeIcon, // az eszköz-katalógus (Kategóriák kezelő)
  'asset-types': CubeIcon,

  // ── Tudásbázis (Mo) — kereszt-app feed, nincs Atlas-menüje, ezért mind ÚJ jel ────────────────────
  spaces: BookOpenIcon,
  categories: TagIcon,
  articles: DocumentTextIcon,
  'article-attachments': PaperClipIcon,
  'article-revisions': ClockIcon, // verzió-történet
  drafts: PencilSquareIcon,
  'knowledge-gaps': QuestionMarkCircleIcon,

  // ── Platform-entitások ────────────────────────────────────────────────────────────────────────────
  external_app: PuzzlePieceIcon,
  // A CSATOLMÁNY-CSALÁD egy jelet visz (gemkapocs) — a jegy-, üzenet-, fejlesztés- és szócikk-melléklet
  // ugyanaz a fogalom, a badge FELIRATA mondja meg, melyiké.
  file: PaperClipIcon,
};

/** Egy entitás-típus ikonja; ismeretlen típusra a semleges fallback (lásd ott). */
export function entityIcon(type: string): EntityIcon {
  return ENTITY_ICONS[type] ?? FALLBACK_ENTITY_ICON;
}
